import { PricingLayout } from "@/components/billing/PricingLayout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";
import { cn } from "@/lib/utils";
import {
  createSubscription,
  getActiveProductsAndPrices,
  StripeProduct,
  StripePrice,
} from "@/services/payments";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface PricingProps {
  mode: "paywall" | "billing";
  className?: string;
}

export default function Pricing({ mode, className }: PricingProps) {
  const { user: authUser } = useAuth();

  const [loading, setLoading] = useState<string | null>(null);
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [prices, setPrices] = useState<StripePrice[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch products and prices on component mount
  useEffect(() => {
    const fetchProductsAndPrices = async () => {
      try {
        setDataLoading(true);
        const data = await getActiveProductsAndPrices();
        setProducts(data.products);
        setPrices(data.prices);
      } catch (err) {
        console.error("Failed to fetch products and prices:", err);
        setError("Failed to load pricing information");
        toast.error("Failed to load pricing information");
      } finally {
        setDataLoading(false);
      }
    };

    fetchProductsAndPrices();
  }, []);

  if (!authUser) return null;

  // Get price for the selected product and interval
  const getPriceForProduct = (productId: string, interval: string) => {
    return prices.find(
      (price) => price.product_id === productId && price.interval === interval,
    );
  };

  const handlePlanAction = async (productId: string, isAnnual: boolean) => {
    const product = products.find((p) => p.product_id === productId);
    const selectedPrice = getPriceForProduct(
      productId,
      isAnnual ? "year" : "month",
    );

    if (!selectedPrice) {
      toast.error("Price information not available for this plan");
      return;
    }

    const planName =
      product?.metadata?.planID || product?.name.toLowerCase() || "unknown";
    setLoading(planName);

    const isTrial = mode === "paywall";
    const redirectUrl = isTrial
      ? `${window.location.origin}/onboarding?from=trial`
      : `${window.location.origin}/`;
    const cancelUrl = isTrial
      ? `${window.location.origin}/start-trial`
      : `${window.location.origin}/plans`;

    try {
      const response = await createSubscription({
        planId: planName,
        priceId: selectedPrice.id,
        billingType: isAnnual ? "annual" : "monthly",
        customerEmail: authUser.email,
        customerName: authUser.name || authUser.email,
        orgId: authUser.org,
        isTrial,
        redirectUrl,
        cancelUrl,
      });
      window.location.href = response.paymentUrl;
    } catch (error) {
      console.error("Error starting trial:", error);
      toast.error("Failed to start trial. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  // Show loading state
  if (dataLoading) {
    return (
      <div
        className={cn(
          "flex items-center justify-center p-4",
          mode === "paywall" ? "min-h-screen bg-gray-50" : "py-12",
          className,
        )}
      >
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="text-gray-600">Loading pricing plans...</span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || products.length === 0) {
    return (
      <div
        className={cn(
          "flex items-center justify-center p-4",
          mode === "paywall" ? "min-h-screen bg-gray-50" : "py-12",
          className,
        )}
      >
        <div className="text-center space-y-4">
          <p className="text-gray-600">
            {error || "No pricing plans available at the moment."}
          </p>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </div>
    );
  }

  // Render unified layout
  return (
    <PricingLayout
      products={products}
      prices={prices}
      onPlanAction={handlePlanAction}
      loading={loading}
      className={className}
      mode={mode}
    />
  );
}
